import Step from 'apollo-react/components/Step';
import StepLabel from 'apollo-react/components/StepLabel';
import Stepper from 'apollo-react/components/Stepper';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CommonModal from '../../../common/CommonModal';
import { Form, withFormik } from 'formik';
import styles from './WorkflowWizard.module.css';
import ButtonGroup from 'apollo-react/components/ButtonGroup';
import Grid from 'apollo-react/components/Grid';
import MenuItem from 'apollo-react/components/MenuItem';
import Paper from 'apollo-react/components/Paper';
import Typography from '../../../common/StyledTypography';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
  FormikDatePicker,
  FormikImageUploadWithPreview,
} from '../../../common/FormikComponent';
import Autocomplete from 'apollo-react/components/Autocomplete';
import { useTranslation } from 'react-i18next';
import LinearProgress from 'apollo-react/components/LinearProgress';
import WorkflowBuilder from './WorkflowBuilder';
import Sidebar from './BuilderSidebar';
import validate from './validators/wizardValidation';
import { WorkflowProvider } from './WorkflowContext';
import useWizardData, {
  WIZARD_INITIAL_VALUES,
} from '../../../../hooks/features/workflows/useWizardData';
import useWizardActions from '../../../../hooks/features/workflows/useWizardActions';
import useUnsavedChangesWarning from '../../../../hooks/ui/navigation/useUnsavedChangesWarning';

const handleSubmit = (values, { setSubmitting }) => {
  setTimeout(() => {
    setSubmitting(false);
  }, 400);
};

const MainWizard = ({
  values,
  content,
  setFieldTouched,
  setFieldError,
  resetForm,
  setValues,
  setFieldValue,
}) => {
  const { wfId, vId: versionId, draftId } = useParams();
  const { t } = useTranslation();
  const steps = [t('workflowWizard.step1'), t('workflowWizard.step2')];
  const workflowBuilderRef = useRef(null);
  const [isDraftSaved, setIsDraftSaved] = useState(false);

  const { navigateBack } = useUnsavedChangesWarning(!isDraftSaved);

  const {
    loading,
    workflowTemplates,
    allWorkflows,
    preJsonData,
    setPreJsonData,
    publishedAtValue,
    setPublishedAtValue,
    originalPublishedAt,
    originalName,
    handleTemplateSelection,
    paymentProviders,
  } = useWizardData({
    versionId,
    draftId,
    values,
    setValues,
    setFieldValue,
    content,
  });

  const selectedPaymentProvider = useMemo(() => {
    if (!values.payment_provider_id) return null;
    const normalizedId = String(values.payment_provider_id);
    const found = paymentProviders.find(
      (provider) => String(provider.id) === normalizedId
    );
    return found || null;
  }, [paymentProviders, values.payment_provider_id]);

  const supportedCurrencies = useMemo(() => {
    const currencies = selectedPaymentProvider?.supported_currencies || [];
    const normalized = currencies
      .map((currency) => String(currency).trim().toUpperCase())
      .filter(Boolean);
    return normalized;
  }, [selectedPaymentProvider]);

  useEffect(() => {
    if (!values.payment_enabled) return;
    if (!values.payment_provider_id) {
      if (values.payment_currency) {
        setFieldValue('payment_currency', '');
      }
      return;
    }
    if (
      values.payment_currency &&
      supportedCurrencies.length > 0 &&
      !supportedCurrencies.includes(values.payment_currency)
    ) {
      setFieldValue('payment_currency', '');
    }
  }, [
    values.payment_enabled,
    values.payment_provider_id,
    values.payment_currency,
    supportedCurrencies,
    setFieldValue,
  ]);

  const {
    activeStep,
    showDiscardModal,
    showPublishModal,
    handleBackStep,
    handleNextStep,
    handleDiscardOpen,
    handleDiscardClose,
    confirmDiscard,
    dataPublishingValidation,
    handlePublishOpen,
    handlePublishClose,
    confirmPublish,
    saveAsDraft,
    submitSaveAsTemplate,
  } = useWizardActions({
    values,
    setValues,
    setFieldTouched,
    setFieldError,
    resetForm,
    versionId,
    draftId,
    wfId,
    navigateBack,
    workflowBuilderRef,
    preJsonData,
    setPreJsonData,
    publishedAtValue,
    setPublishedAtValue,
    originalPublishedAt,
    originalName,
    setIsDraftSaved,
  });

  return (
    <>
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Stepper box activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label} className={styles.noBorder}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length - 1 ? <Sidebar /> : ''}
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <div className={styles.wfProgress}>
            <LinearProgress
              variant="determinate"
              className={'progress-bar-workflow'}
              value={activeStep !== steps.length - 1 ? 50 : 100}
            />
          </div>

          {loading ? (
            <Typography variant="bodySmall" className={styles.verticalMargin}>
              {t('workflowWizard.loading')}
            </Typography>
          ) : (
            <Form noValidate>
              {activeStep !== steps.length - 1 ? (
                <div className={styles.section}>
                  <Paper className={styles.paper}>
                    <FormikTextField
                      fullWidth
                      name="name"
                      label={t('workflowWizard.workflowName')}
                      required
                    />

                    <FormikImageUploadWithPreview
                      name="workflowImage"
                      label={t('workflowWizard.uploadImage')}
                      helperText={t('workflowWizard.uploadImageHelper')}
                      accept=".jpg,.jpeg,.png,.gif"
                      isAdminUpload={true}
                      required={false}
                    />

                    <FormikTextField
                      fullWidth
                      name="description"
                      label={t('workflowWizard.description')}
                      multiline
                    />

                    <FormikSwitch
                      name="payment_enabled"
                      checked={values.payment_enabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setFieldValue('payment_enabled', enabled);
                        if (!enabled) {
                          setFieldValue('payment_provider_id', '');
                          setFieldValue('payment_currency', '');
                        }
                      }}
                      label={t('workflowWizard.paymentEnabled')}
                      key="payment_enabled"
                    />

                    {values.payment_enabled && (
                      <>
                        <FormikSelect
                          name="payment_provider_id"
                          label={t('workflowWizard.paymentProvider')}
                          fullWidth
                          required
                          disabled={paymentProviders.length === 0}
                        >
                          <MenuItem value="">
                            {t('workflowWizard.noPaymentProvider')}
                          </MenuItem>
                          {paymentProviders?.map((provider) => (
                            <MenuItem key={provider.id} value={provider.id}>
                              {provider.display_name}
                            </MenuItem>
                          ))}
                        </FormikSelect>

                        <FormikSelect
                          key={`currency-${values.payment_provider_id || 'none'}-${supportedCurrencies.join('-')}`}
                          name="payment_currency"
                          label={t('workflowWizard.paymentCurrency')}
                          fullWidth
                          required
                          disabled={
                            !values.payment_provider_id ||
                            supportedCurrencies.length === 0
                          }
                        >
                          <MenuItem value="">
                            {t('workflowWizard.selectPaymentCurrency')}
                          </MenuItem>
                          {supportedCurrencies.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currency}
                            </MenuItem>
                          ))}
                        </FormikSelect>
                      </>
                    )}

                    <input type="hidden" name="versionId" value={versionId} />

                    <FormikSelect
                      name="workflowTemplateId"
                      label={t('workflowWizard.selectWorkflowTemplate')}
                      fullWidth
                      onChange={(e) => {
                        handleTemplateSelection(e.target.value);
                      }}
                    >
                      <MenuItem value="">
                        {t('workflowWizard.selectAWorkflowTemplate')}
                      </MenuItem>
                      {workflowTemplates?.map((workflowTemplate) => (
                        <MenuItem
                          key={workflowTemplate.id}
                          value={workflowTemplate.id}
                        >
                          {workflowTemplate.name}
                        </MenuItem>
                      ))}
                    </FormikSelect>

                    <div className={styles.marginTop16}>
                      <Autocomplete
                        label={t('selectLinkableWorkflows')}
                        source={allWorkflows
                          .filter((wf) => wf.id !== parseInt(wfId))
                          .map((wf) => ({
                            label: wf.name,
                            value: wf.id,
                          }))}
                        value={values.linkable_workflow_ids || []}
                        onChange={(event, newValue) =>
                          setFieldValue('linkable_workflow_ids', newValue)
                        }
                        multiple
                        chipColor="white"
                        helperText={t('selectLinkableWorkflowsHelper')}
                        fullWidth
                      />
                    </div>

                    <FormikSwitch
                      checked={values.saveAsTemplate === 1}
                      onChange={(e) => {
                        setFieldValue(
                          'saveAsTemplate',
                          e.target.checked ? 1 : 0
                        );
                      }}
                      name="saveAsTemplate"
                      label={t('workflowWizard.saveAsTemplate')}
                    />
                  </Paper>

                  <ButtonGroup
                    alignItems="right"
                    className={styles.marginTop16}
                    buttonProps={[
                      {
                        variant: 'secondary',
                        label: t('workflowWizard.previous'),
                        onClick: handleBackStep,
                        disabled: true,
                        className: styles.buttons,
                      },
                      {
                        variant: 'primary',
                        label: t('workflowWizard.discard'),
                        status: 'danger',
                        onClick: handleDiscardOpen,
                        className: styles.buttons,
                      },
                      {
                        variant: 'primary',
                        label: t('workflowWizard.next'),
                        onClick: () => handleNextStep(validate, values),
                        type: 'button',
                        className: styles.buttons,
                      },
                    ]}
                  />
                </div>
              ) : activeStep !== steps.length ? (
                <div className={styles.marginTop16}>
                  <Paper className={styles.paper}>
                    <WorkflowBuilder
                      ref={workflowBuilderRef}
                      paymentProviderId={values.payment_provider_id}
                      paymentCurrency={values.payment_currency}
                      paymentEnabled={values.payment_enabled}
                      paymentProviders={paymentProviders}
                    />
                  </Paper>

                  <ButtonGroup
                    alignItems="right"
                    className={styles.marginTop16}
                    buttonProps={[
                      {
                        variant: 'secondary',
                        label: t('workflowWizard.previous'),
                        onClick: handleBackStep,
                        className: styles.buttons,
                      },
                      {
                        variant: 'primary',
                        label: t('workflowWizard.discard'),
                        status: 'danger',
                        onClick: handleDiscardOpen,
                        className: styles.buttons,
                      },
                      {
                        variant: 'primary',
                        label: t('workflowWizard.validate'),
                        onClick: dataPublishingValidation,
                        type: 'button',
                        className: styles.buttons,
                      },
                      {
                        variant: 'primary',
                        label: t('workflowWizard.saveAsDraft'),
                        onClick: () => saveAsDraft(),
                        type: 'button',
                        className: styles.buttons,
                      },
                      {
                        variant: 'primary',
                        label:
                          values.saveAsTemplate === 0
                            ? t('workflowWizard.publish')
                            : t('workflowWizard.save'),
                        onClick: () =>
                          values.saveAsTemplate === 0
                            ? handlePublishOpen()
                            : submitSaveAsTemplate(),
                        type: 'button',
                        className: styles.buttons,
                      },
                    ]}
                  />
                </div>
              ) : (
                <div>
                  <Typography
                    variant="bodySmall"
                    className={styles.verticalMargin}
                  >
                    {t('workflowWizard.allStepsCompleted')}
                  </Typography>
                </div>
              )}
            </Form>
          )}
        </Grid>
      </Grid>

      <CommonModal
        open={showDiscardModal}
        variant="warning"
        title={t('workflowWizard.confirmDiscard')}
        message={t('workflowWizard.discardMessage')}
        onClose={() => handleDiscardClose()}
        buttonProps={[
          { label: t('workflowWizard.goBack'), onClick: handleDiscardClose },
          {
            label: t('workflowWizard.confirm'),
            onClick: confirmDiscard,
          },
        ]}
        id="warning"
      />

      <CommonModal
        open={showPublishModal}
        variant="success"
        title={t('workflowWizard.confirmPublish')}
        className={styles.modal}
        id="success"
        onClose={() => handlePublishClose()}
        buttonProps={[
          { label: t('workflowWizard.goBack'), onClick: handlePublishClose },
          {
            label: t('workflowWizard.publish'),
            onClick: () => confirmPublish(),
          },
        ]}
      >
        <FormikDatePicker
          dateFormat="YYYY-MM-DD"
          fullWidth
          name="publishedAt"
          defaultValue={publishedAtValue}
          placeholder="mm/dd/yyyy"
          label={t('workflowWizard.dateLabel')}
          disablePast
          required
          onChange={(date) => {
            setFieldValue('publishedAt', date);
            setPublishedAtValue(date);
          }}
        />
      </CommonModal>
    </>
  );
};

const MainWizardWithFormik = withFormik({
  validate,
  mapPropsToValues: () => WIZARD_INITIAL_VALUES,
  handleSubmit,
  validateOnChange: false,
  validateOnBlur: false,
})(MainWizard);

const MainWizardWithProvider = (props) => (
  <WorkflowProvider>
    <MainWizardWithFormik {...props} />
  </WorkflowProvider>
);

export default MainWizardWithProvider;
